import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  VStack,
  HStack,
  Text,
  Spinner,
  Center,
  TableContainer,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from '@chakra-ui/react'
import { motion } from 'framer-motion'
import { FiFile, FiShare2, FiImage, FiFolder } from 'react-icons/fi'
import { FileItem } from '@/types'
import { apiService } from '@/services/api'
import { useTrackNavigation } from '@/hooks/useTrackNavigation'
import { useAppTranslation } from '@/contexts/LanguageContext'
import { containerVariants, listItemVariants } from '@/theme/animations'
import { MotionBox } from '@/components/Common/MotionBox'

const MotionTr = motion(Tr as any)

export default function SharedWithMePage() {
  const navigate = useNavigate()
  const { t, formatFileSize, formatDateTime } = useAppTranslation(['files', 'common'])
  const [files, setFiles] = useState<FileItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useTrackNavigation('navigation.shared_with_me')

  useEffect(() => {
    apiService.getSharedWithMe()
      .then(setFiles)
      .catch(() => setError('Failed to load shared files'))
      .finally(() => setIsLoading(false))
  }, [])

  const getFileIcon = (mimeType: string) => {
    if (mimeType?.startsWith('image/')) return <FiImage color="#805ad5" />
    if (mimeType === 'application/pdf') return <FiFile color="#e53e3e" />
    return <FiFile />
  }

  if (isLoading) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <Spinner size="lg" color="brand.500" thickness="3px" />
        </Center>
      </MotionBox>
    )
  }

  if (error) {
    return (
      <MotionBox initial="initial" animate="animate" variants={containerVariants} p={6} h="full">
        <Center h="full">
          <Text color="red.500">{error}</Text>
        </Center>
      </MotionBox>
    )
  }

  return (
    <MotionBox
      initial="initial"
      animate="animate"
      variants={containerVariants}
      p={6}
      h="full"
      overflow="auto"
    >
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack spacing={3}>
          <Box color="brand.500">
            <FiShare2 size="22px" />
          </Box>
          <Text fontSize="xl" fontWeight="bold">
            {t('navigation.sharedWithMe', { ns: 'common' })}
          </Text>
          <Badge colorScheme="brand" borderRadius="full" px={2}>
            {files.length}
          </Badge>
        </HStack>

        {/* Files Table */}
        {files.length === 0 ? (
          <Center py={20}>
            <VStack spacing={4}>
              <Box color="gray.400">
                <FiShare2 size="48px" />
              </Box>
              <VStack spacing={2}>
                <Text fontSize="lg" fontWeight="medium" color="gray.600">
                  {t('sharedWithMe.empty', { ns: 'files' })}
                </Text>
                <Text fontSize="sm" color="gray.500" textAlign="center">
                  {t('sharedWithMe.emptyDescription', { ns: 'files' })}
                </Text>
              </VStack>
            </VStack>
          </Center>
        ) : (
          <MotionBox variants={listItemVariants}>
            <TableContainer
              bg="white"
              borderRadius="2xl"
              border="1px"
              borderColor="gray.200"
              boxShadow="sm"
              overflow="hidden"
            >
              <Table variant="simple">
                <Thead bg="gray.50">
                  <Tr>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.name', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.location', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.size', { ns: 'common' }).toUpperCase()}
                    </Th>
                    <Th color="gray.700" fontWeight="semibold" fontSize="sm" letterSpacing="wide" borderColor="gray.200" py={3}>
                      {t('labels.modified', { ns: 'common' }).toUpperCase()}
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {files.map((file, index) => (
                    <MotionTr
                      key={file.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2, delay: index * 0.03 }}
                      cursor="pointer"
                      onClick={() => navigate(`/preview/${file.id}`)}
                      _hover={{ bg: 'blue.25' }}
                    >
                      <Td borderColor="gray.200" py={3}>
                        <HStack spacing={3}>
                          <Box color="gray.600">{getFileIcon(file.mime_type)}</Box>
                          <Text fontSize="sm" fontWeight="medium" color="gray.800" noOfLines={1}>
                            {file.display_name || file.original_name}
                          </Text>
                        </HStack>
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        {file.folder ? (
                          <HStack spacing={1} color="gray.500">
                            <FiFolder size="13px" />
                            <Text fontSize="sm">{file.folder.name}</Text>
                          </HStack>
                        ) : (
                          <Text fontSize="sm" color="gray.400">—</Text>
                        )}
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        <Text fontSize="sm" color="gray.600">{formatFileSize(file.file_size)}</Text>
                      </Td>
                      <Td borderColor="gray.200" py={3}>
                        <Text fontSize="sm" color="gray.600" whiteSpace="nowrap">
                          {formatDateTime(new Date(file.updated_at))}
                        </Text>
                      </Td>
                    </MotionTr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </MotionBox>
        )}
      </VStack>
    </MotionBox>
  )
}
